import { StaticTimeline } from './StaticTimeline';
import clamp from '../../utils/math/clamp';
/**
 * Timeline is an animation helper.
 */
export class Timeline extends StaticTimeline {
    constructor(initialProp, init = true) {
        super(initialProp, false);
        // set default variables
        this._isPlaying = false;
        this._isReversed = false;
        this._isPaused = false;
        this._animationFrame = undefined;
        this._animationFrameLastTime = 0;
        if (init) {
            this.init();
        }
    }
    /**
     * Get default properties
     */
    _getDefaultProp() {
        return Object.assign(Object.assign({}, super._getDefaultProp()), { duration: 1000, reset: false, destroyOnEnd: false });
    }
    /**
     * Check if timeline is playing
     */
    get isPlaying() {
        return typeof this._animationFrame !== 'undefined';
    }
    /**
     * Check if timeline is reversed
     */
    get isReversed() {
        return this._isReversed;
    }
    /**
     * Check if timeline is paused
     */
    get isPaused() {
        return this._isPaused;
    }
    /**
     * Play the timeline
     */
    play() {
        if (this._destroyed) {
            return;
        }
        if (this.prop.reset && this.progress === 1) {
            this._progress = 0;
        }
        this._isReversed = false;
        this._isPaused = false;
        if (!this.isPlaying) {
            this._animationFrameLastTime = +new Date();
            this._animate();
        }
    }
    /**
     * Play reversed timeline
     */
    reverse() {
        if (this._destroyed) {
            return;
        }
        if (this.prop.reset && this.progress === 0) {
            this._progress = 1;
        }
        this._isReversed = true;
        this._isPaused = false;
        if (!this.isPlaying) {
            this._animationFrameLastTime = +new Date();
            this._animate();
        }
    }
    /**
     * Pause animation
     */
    pause() {
        if (this._destroyed) {
            return;
        }
        this._isPaused = true;
        if (this._animationFrame) {
            window.cancelAnimationFrame(this._animationFrame);
        }
        this._animationFrame = undefined;
    }
    /**
     * Start animation
     */
    _animate() {
        // stop if animation is paused
        if (this._isPaused) {
            return;
        }
        const { isReversed } = this;
        // calculate difference between frames
        const currentTime = +new Date();
        const frameDiff = Math.abs(this._animationFrameLastTime - currentTime);
        this._animationFrameLastTime = currentTime;
        // calculate current progress
        const progressIterator = frameDiff / this.prop.duration / (isReversed ? -1 : 1);
        const progressTarget = clamp(this.progress + progressIterator, [0, 1]);
        this.progress = progressTarget;
        // end animation
        if ((progressTarget === 1 && !isReversed)
            || (progressTarget === 0 && isReversed)) {
            this._isReversed = false;
            this._isPaused = false;
            this._animationFrame = undefined;
            return;
        }
        // continue animation
        this._animationFrame = window.requestAnimationFrame(this._animate.bind(this));
    }
    /**
     * Events on progress
     */
    _handleProgress() {
        super._handleProgress();
        const progress = this._progress;
        // launch callbacks
        if (progress === 0) {
            this.callbacks.tbt('start', false);
        }
        else if (progress === 1) {
            this.callbacks.tbt('end', false);
            if (this.prop.destroyOnEnd) {
                this.destroy();
            }
        }
    }
    /**
     * Destroy the timeline
     */
    _destroy() {
        this.pause();
        super._destroy();
    }
}
